import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import { WsafeReviewStatus } from './WsafeReviewStatus';
import { apiCall } from '../utils/api';
import { AlertTriangle, Info, Target, ArrowRight, Plus, Loader2, ClipboardCheck } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const RiskMatrix = ({ onRiskSelect, selectedRisk: externalSelectedRisk, showDialog = false, onClose }) => {
  const { toast } = useToast();
  const [hoveredCell, setHoveredCell] = useState(null);
  const [showCAPADialog, setShowCAPADialog] = useState(false);
  const [sentTickets, setSentTickets] = useState([]);

  React.useEffect(() => {
    apiCall('/api/wsafe-si/candidates?source_type=risk_matrix&limit=20')
      .then(d => setSentTickets(d.candidates.map(c => ({ source_id: c.source_id, title: (c.description || '').split(':')[0] }))))
      .catch(() => {});
  }, []);
  const [creatingCAPA, setCreatingCAPA] = useState(false);
  const [internalSelectedRisk, setInternalSelectedRisk] = useState(null);
  const [capaForm, setCAPAForm] = useState({
    title: '',
    description: '',
    location: '',
    hazard_type: 'default'
  });

  // Use external selectedRisk if provided, otherwise use internal state
  const selectedRisk = externalSelectedRisk !== undefined ? externalSelectedRisk : internalSelectedRisk;

  // Risk matrix definitions
  const severityLevels = [
    { id: 5, label: 'Catastrophic', description: 'Death or permanent disability', color: 'bg-red-900' },
    { id: 4, label: 'Major', description: 'Serious injury requiring hospitalization', color: 'bg-red-700' },
    { id: 3, label: 'Moderate', description: 'Medical treatment required', color: 'bg-orange-500' },
    { id: 2, label: 'Minor', description: 'First aid treatment only', color: 'bg-yellow-500' },
    { id: 1, label: 'Negligible', description: 'No injury or minor discomfort', color: 'bg-green-500' }
  ];

  const likelihoodLevels = [
    { id: 1, label: 'Rare', description: 'May occur only in exceptional circumstances' },
    { id: 2, label: 'Unlikely', description: 'Could occur but not expected' },
    { id: 3, label: 'Possible', description: 'Might occur at some time' },
    { id: 4, label: 'Likely', description: 'Will probably occur in most circumstances' },
    { id: 5, label: 'Almost Certain', description: 'Expected to occur in most circumstances' }
  ];

  // Calculate risk score and level
  const calculateRisk = (severity, likelihood) => {
    const score = severity * likelihood;
    if (score >= 20) return { level: 'Critical', color: 'bg-red-600 text-white', priority: 'critical' };
    if (score >= 12) return { level: 'High', color: 'bg-orange-500 text-white', priority: 'high' };
    if (score >= 6) return { level: 'Medium', color: 'bg-yellow-400 text-slate-900', priority: 'medium' };
    return { level: 'Low', color: 'bg-green-500 text-white', priority: 'low' };
  };

  const handleCellClick = (severity, likelihood) => {
    const risk = calculateRisk(severity, likelihood);
    const riskData = {
      severity,
      likelihood,
      score: severity * likelihood,
      ...risk
    };
    
    // Update internal state for standalone usage
    setInternalSelectedRisk(riskData);
    
    // Call external handler if provided
    if (onRiskSelect) {
      onRiskSelect(riskData);
    }
    
    toast({
      title: `Risk Level: ${risk.level}`,
      description: `Score: ${severity * likelihood} (Severity: ${severity}, Likelihood: ${likelihood})`
    });
  };

  const handleCreateCAPA = async () => {
    if (!selectedRisk || !capaForm.title) return;
    
    setCreatingCAPA(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(`${API}/api/capa/from-risk`, {
        title: capaForm.title,
        description: capaForm.description || `Risk identified with score ${selectedRisk.score} (${selectedRisk.level})`,
        risk_score: selectedRisk.score,
        risk_level: selectedRisk.priority,
        severity: selectedRisk.severity,
        likelihood: selectedRisk.likelihood,
        source: 'hazard',
        hazard_type: capaForm.hazard_type,
        location: capaForm.location
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      // Also save the risk assessment to history
      await saveRiskAssessment(capaForm.title, capaForm.description, capaForm.location, capaForm.hazard_type);
      
      setSentTickets(prev => [{ source_id: response.data.source_id, title: capaForm.title }, ...prev]);
      toast({
        title: 'Sent to HazardLoop via WSafe.SI',
        description: 'A competent person reviews it in WSafe.SI. The ticket opens when it is confirmed.'
      });
      
      setShowCAPADialog(false);
      setCAPAForm({ title: '', description: '', location: '', hazard_type: 'default' });
    } catch (error) {
      console.error('Error creating CAPA:', error);
      toast({ title: 'Error', description: 'Could not send to WSafe.SI', variant: 'destructive' });
    } finally {
      setCreatingCAPA(false);
    }
  };

  const saveRiskAssessment = async (title, description, location, hazard_type) => {
    if (!selectedRisk) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(`${API}/api/risk-history/assessments`, {
        title: title || `Risk Assessment - ${new Date().toLocaleDateString()}`,
        description: description || `Risk score ${selectedRisk.score} (${selectedRisk.level})`,
        severity: selectedRisk.severity,
        likelihood: selectedRisk.likelihood,
        risk_score: selectedRisk.score,
        risk_level: selectedRisk.priority,
        location: location || '',
        hazard_type: hazard_type || 'general'
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      return true;
    } catch (error) {
      console.error('Error saving risk assessment:', error);
      return false;
    }
  };

  const MatrixContent = () => (
    <div className="space-y-6">
      {/* Risk Matrix Grid */}
      <div className="overflow-x-auto">
        <div className="min-w-[500px]">
          {/* Header Row - Likelihood */}
          <div className="flex">
            <div className="w-28 shrink-0" /> {/* Empty corner */}
            <div className="flex-1 grid grid-cols-5 gap-1 mb-2">
              {likelihoodLevels.map(l => (
                <div key={l.id} className="text-center text-xs font-medium text-slate-600 px-1">
                  <div>{l.label}</div>
                  <div className="text-[10px] text-slate-400">{l.id}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Matrix Rows - Severity */}
          {[...severityLevels].map(s => (
            <div key={s.id} className="flex mb-1">
              {/* Severity Label */}
              <div className="w-28 shrink-0 flex items-center justify-end pr-2">
                <div className="text-right">
                  <div className="text-xs font-medium text-slate-700">{s.label}</div>
                  <div className="text-[10px] text-slate-400">{s.id}</div>
                </div>
              </div>
              
              {/* Risk Cells */}
              <div className="flex-1 grid grid-cols-5 gap-1">
                {likelihoodLevels.map(l => {
                  const risk = calculateRisk(s.id, l.id);
                  const isHovered = hoveredCell === `${s.id}-${l.id}`;
                  const isSelected = selectedRisk?.severity === s.id && selectedRisk?.likelihood === l.id;
                  
                  return (
                    <button
                      key={`${s.id}-${l.id}`}
                      className={`
                        h-12 rounded-lg flex items-center justify-center text-sm font-bold
                        transition-all duration-150 cursor-pointer
                        ${risk.color}
                        ${isHovered ? 'ring-2 ring-offset-2 ring-blue-500 scale-105' : ''}
                        ${isSelected ? 'ring-2 ring-offset-2 ring-slate-900' : ''}
                      `}
                      onMouseEnter={() => setHoveredCell(`${s.id}-${l.id}`)}
                      onMouseLeave={() => setHoveredCell(null)}
                      onClick={() => handleCellClick(s.id, l.id)}
                      data-testid={`risk-cell-${s.id}-${l.id}`}
                    >
                      {s.id * l.id}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Axis Labels */}
          <div className="flex mt-4">
            <div className="w-28 shrink-0 flex items-center justify-center">
              <span className="text-xs text-slate-500">SEVERITY</span>
            </div>
            <div className="flex-1 text-center">
              <span className="text-xs text-slate-500">LIKELIHOOD</span>
            </div>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap justify-center gap-3">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-green-500" />
          <span className="text-xs text-slate-600">Low (1-5)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-yellow-400" />
          <span className="text-xs text-slate-600">Medium (6-11)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-orange-500" />
          <span className="text-xs text-slate-600">High (12-19)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-red-600" />
          <span className="text-xs text-slate-600">Critical (20-25)</span>
        </div>
      </div>

      {/* Selected Risk Info */}
      {selectedRisk && (
        <Card className="bg-slate-50">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-slate-600">Selected Risk Assessment</div>
                <div className="flex items-center gap-3 mt-1">
                  <Badge className={selectedRisk.color}>{selectedRisk.level}</Badge>
                  <span className="text-slate-900 font-medium">
                    Score: {selectedRisk.score}
                  </span>
                </div>
              </div>
              <div className="text-right text-sm text-slate-600">
                <div>Severity: {severityLevels.find(s => s.id === selectedRisk.severity)?.label}</div>
                <div>Likelihood: {likelihoodLevels.find(l => l.id === selectedRisk.likelihood)?.label}</div>
              </div>
            </div>
            {/* Create CAPA Button */}
            <div className="mt-4 pt-4 border-t">
              <Button 
                onClick={() => setShowCAPADialog(true)}
                className="w-full bg-blue-600 hover:bg-blue-700"
                data-testid="create-capa-from-risk-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Send to HazardLoop for Corrective Action
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {sentTickets.length > 0 && (
        <Card className="mt-4" data-testid="hazardloop-sent-list">
          <CardHeader>
            <CardTitle className="text-base">Corrective actions in HazardLoop</CardTitle>
            <CardDescription>Tickets are created and tracked in HazardLoop through WSafe.SI.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {sentTickets.map(t => (
              <div key={t.source_id} className="p-3 border rounded-lg" data-testid={`hazardloop-item-${t.source_id}`}>
                <p className="font-medium text-sm">{t.title}</p>
                <WsafeReviewStatus sourceType="risk_matrix" sourceId={t.source_id} />
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Create CAPA Dialog */}
      <Dialog open={showCAPADialog} onOpenChange={setShowCAPADialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-blue-600" />
              Send to HazardLoop
            </DialogTitle>
            <DialogDescription>
              Goes to the WSafe.SI review queue. HazardLoop opens the ticket when a competent person confirms it.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {selectedRisk && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <Badge className={selectedRisk.color}>{selectedRisk.level}</Badge>
                  <span className="text-sm text-slate-600">Score: {selectedRisk.score}</span>
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Priority: {selectedRisk.level === 'Critical' || selectedRisk.level === 'High' ? 'Verification Required' : 'Standard'}
                </p>
              </div>
            )}
            
            <div>
              <label className="text-sm font-medium text-slate-700">Title *</label>
              <Input
                value={capaForm.title}
                onChange={(e) => setCAPAForm({...capaForm, title: e.target.value})}
                placeholder="Brief description of the hazard"
                className="mt-1"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700">Description</label>
              <Textarea
                value={capaForm.description}
                onChange={(e) => setCAPAForm({...capaForm, description: e.target.value})}
                placeholder="Detailed description of the risk and required actions"
                className="mt-1"
                rows={3}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700">Location</label>
              <Input
                value={capaForm.location}
                onChange={(e) => setCAPAForm({...capaForm, location: e.target.value})}
                placeholder="e.g., Building A, Floor 2"
                className="mt-1"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700">Hazard Type</label>
              <Select 
                value={capaForm.hazard_type} 
                onValueChange={(v) => setCAPAForm({...capaForm, hazard_type: v})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">General</SelectItem>
                  <SelectItem value="fall_protection">Fall Protection</SelectItem>
                  <SelectItem value="scaffolding">Scaffolding</SelectItem>
                  <SelectItem value="electrical">Electrical</SelectItem>
                  <SelectItem value="confined_space">Confined Space</SelectItem>
                  <SelectItem value="hazardous_materials">Hazardous Materials</SelectItem>
                  <SelectItem value="machine_guarding">Machine Guarding</SelectItem>
                  <SelectItem value="ppe">PPE</SelectItem>
                  <SelectItem value="fire_safety">Fire Safety</SelectItem>
                  <SelectItem value="ergonomics">Ergonomics</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowCAPADialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreateCAPA}
                data-testid="submit-capa-from-risk-btn"
                disabled={creatingCAPA || !capaForm.title}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {creatingCAPA ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
                ) : (
                  <><Plus className="w-4 h-4 mr-2" />Send to HazardLoop</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Control Recommendations */}
      <div className="space-y-3">
        <h4 className="font-medium text-slate-900 flex items-center gap-2">
          <Target className="w-4 h-4" />
          Risk Control Hierarchy
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
          {[
            { label: 'Eliminate', desc: 'Remove the hazard', color: 'bg-green-100 border-green-300' },
            { label: 'Substitute', desc: 'Replace with safer alternative', color: 'bg-emerald-100 border-emerald-300' },
            { label: 'Engineering', desc: 'Isolate people from hazard', color: 'bg-yellow-100 border-yellow-300' },
            { label: 'Administrative', desc: 'Change work practices', color: 'bg-orange-100 border-orange-300' },
            { label: 'PPE', desc: 'Last line of defense', color: 'bg-red-100 border-red-300' }
          ].map((control, idx) => (
            <div key={control.label} className={`p-3 rounded-lg border ${control.color}`}>
              <div className="flex items-center gap-1">
                <span className="font-medium text-sm text-slate-900">{idx + 1}. {control.label}</span>
              </div>
              <p className="text-xs text-slate-600 mt-1">{control.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  if (showDialog) {
    return (
      <Dialog open={showDialog} onOpenChange={onClose}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Risk Assessment Matrix
            </DialogTitle>
            <DialogDescription>
              Click on a cell to assess risk based on severity and likelihood
            </DialogDescription>
          </DialogHeader>
          <MatrixContent />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-500" />
          Risk Assessment Matrix
        </CardTitle>
        <CardDescription>
          Evaluate risks by selecting severity and likelihood
        </CardDescription>
      </CardHeader>
      <CardContent>
        <MatrixContent />
      </CardContent>
    </Card>
  );
};

// Standalone Risk Selector Component (for forms)
export const RiskSelector = ({ value, onChange, label = "Risk Level" }) => {
  const [showMatrix, setShowMatrix] = useState(false);

  const getRiskBadge = (risk) => {
    if (!risk) return null;
    return (
      <Badge className={risk.color}>
        {risk.level} (Score: {risk.score})
      </Badge>
    );
  };

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-slate-700">{label}</label>
      <div className="flex items-center gap-3">
        <Button 
          type="button"
          variant="outline" 
          onClick={() => setShowMatrix(true)}
          className="flex-1 justify-between"
        >
          {value ? (
            <span className="flex items-center gap-2">
              {getRiskBadge(value)}
            </span>
          ) : (
            <span className="text-slate-500">Click to assess risk...</span>
          )}
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
      
      <RiskMatrix 
        showDialog={showMatrix}
        onClose={() => setShowMatrix(false)}
        selectedRisk={value}
        onRiskSelect={(risk) => {
          onChange(risk);
          setShowMatrix(false);
        }}
      />
    </div>
  );
};

export default RiskMatrix;
